import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Check, ShoppingBag, Sparkles, Truck, ShieldCheck, Gift, Loader2, Heart, ArrowRight, Instagram, PenLine } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { useCart } from "@/context/CartContext";
import { useInstagram } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ParcelCard } from "@/components/wp/ParcelCard";
import { ProductGallery } from "@/components/wp/ProductGallery";
import { Reveal } from "@/components/wp/Reveal";
import { toast } from "sonner";

export default function ParcelDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useGet(`/parcels/${slug}`, [slug]);
  const { addItem, addRecentlyViewed } = useCart();
  const [activeImg, setActiveImg] = useState(0);
  const [preview, setPreview] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const parcel = data?.parcel;
  const included = data?.included || [];
  const related = data?.related || [];
  const ig = useInstagram();

  useEffect(() => {
    if (parcel) addRecentlyViewed({ ...parcel, type: "parcel" });
    setActiveImg(0); setPreview(null);
  }, [parcel, addRecentlyViewed]);

  const onAdd = () => {
    addItem({ id: parcel.id, name: parcel.name, price: parcel.price, image: parcel.image, type: "parcel" });
    toast.success(`${parcel.name} added 💝`);
  };

  const generatePreview = async () => {
    setGenLoading(true);
    try {
      const { data: res } = await api.post("/ai/preview", { items: parcel.item_ids, description: included.map((p) => p.name).join(", ") });
      setPreview(res.image);
      toast.success("Here's how it could look ✨");
    } catch {
      toast.error("We couldn't create that preview just now.");
    } finally {
      setGenLoading(false);
    }
  };

  if (loading) return (
    <div className="wp-container grid gap-8 py-10 lg:grid-cols-2">
      <Skeleton className="aspect-square w-full rounded-wp-xl" />
      <div className="space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-6 w-1/2" /><Skeleton className="h-24 w-full" /></div>
    </div>
  );
  if (!parcel) return <div className="wp-container py-20 text-center">Parcel not found. <Link to="/shop" className="text-wp-rose-strong underline">Back to shop</Link></div>;

  const gallery = preview
    ? [preview, parcel.image, ...(parcel.images || [])]
    : [parcel.image, ...(parcel.images || [])];

  return (
    <div className="wp-container py-8 sm:py-10">
      <nav className="mb-5 text-xs text-wp-ink-muted"><Link to="/" className="hover:text-wp-rose-strong">Home</Link> / <Link to="/shop" className="hover:text-wp-rose-strong">Shop</Link> / <span className="text-wp-ink">{parcel.name}</span></nav>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Gallery */}
        <ProductGallery
          images={gallery}
          alt={parcel.name}
          testid="product-gallery"
          badge={preview ? <Badge className="border-0 bg-wp-ocean text-white"><Sparkles className="mr-1 h-3 w-3" /> Styled preview</Badge> : null}
        />

        {/* Info */}
        <div>
          {parcel.badge && <Badge className="mb-2 bg-wp-blush-100 text-wp-rose-strong border-0">{parcel.badge}</Badge>}
          <h1 className="font-display text-3xl sm:text-4xl">{parcel.name}</h1>
          <p className="mt-1 text-wp-ink-muted">{parcel.tagline}</p>
          <div className="mt-2 flex items-center gap-1 text-sm text-wp-ink-muted">
            <Star className="h-4 w-4 fill-wp-rose text-wp-rose" /> <span className="price-num">{parcel.rating}</span> <span>({parcel.reviews_count} reviews)</span>
          </div>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="price-num font-display text-3xl text-wp-rose-strong">{formatINR(parcel.price)}</span>
            {parcel.compare_at && <span className="price-num text-lg text-wp-ink-muted line-through">{formatINR(parcel.compare_at)}</span>}
            {parcel.compare_at && <Badge className="bg-wp-success/15 text-wp-success border-0">Save {formatINR(parcel.compare_at - parcel.price)}</Badge>}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-wp-ink-muted">{parcel.description}</p>

          {/* What's included */}
          <div className="mt-6 rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft">
            <h3 className="flex items-center gap-2 font-semibold"><Gift className="h-4 w-4 text-wp-rose-strong" /> What’s inside this parcel</h3>
            <ul className="mt-3 space-y-2">
              {parcel.includes.map((name, i) => (
                <li key={i} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-wp-success" /> {name}</li>
              ))}
            </ul>
            {included.length > 0 && (
              <div className="mt-4 flex gap-2 overflow-x-auto">
                {included.map((p) => (
                  <Link to={`/product/${p.id}`} key={p.id} className="flex-shrink-0">
                    <img src={p.image} alt={p.name} className="h-16 w-16 rounded-wp-sm border border-border object-cover" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* CTA — single clear primary */}
          <div className="mt-6">
            <Button onClick={onAdd} data-testid="parcel-add-to-cart-button" className="w-full rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-105">
              <ShoppingBag className="mr-2 h-5 w-5" /> Add to Cart · {formatINR(parcel.price)}
            </Button>
            <button onClick={generatePreview} disabled={genLoading} data-testid="parcel-ai-preview-button"
              className="mx-auto mt-3 flex items-center justify-center gap-1.5 text-sm font-medium text-wp-ocean transition-colors hover:text-wp-rose-strong disabled:opacity-50">
              {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {genLoading ? "Styling your preview…" : "See a styled preview"}
            </button>
          </div>

          {/* trust */}
          <div className="mt-5 grid grid-cols-3 gap-3 text-center text-xs text-wp-ink-muted">
            <div className="rounded-wp-md border border-border bg-card p-3"><Truck className="mx-auto h-5 w-5 text-wp-rose-strong" /><p className="mt-1">Ships 24–48h</p></div>
            <div className="rounded-wp-md border border-border bg-card p-3"><Gift className="mx-auto h-5 w-5 text-wp-rose-strong" /><p className="mt-1">Gift-ready</p></div>
            <div className="rounded-wp-md border border-border bg-card p-3"><ShieldCheck className="mx-auto h-5 w-5 text-wp-rose-strong" /><p className="mt-1">Hand-packed</p></div>
          </div>
        </div>
      </div>

      {/* Instagram CTA */}
      {ig.enabled && (
        <a href={ig.url} target="_blank" rel="noreferrer" data-testid="product-instagram-cta"
          className="mt-10 flex items-center gap-4 rounded-wp-lg border border-border bg-wp-blush-50 p-5 shadow-wp-soft transition-transform hover:-translate-y-0.5">
          <span className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-full bg-white text-wp-rose-strong shadow"><Instagram className="h-6 w-6" /></span>
          <div className="flex-1">
            <p className="text-sm font-semibold">See real customer photos & behind the scenes</p>
            <p className="text-xs text-wp-ink-muted">Follow us on Instagram {ig.handle}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-wp-rose-strong" />
        </a>
      )}

      {/* Related / AI recommendations */}
      {related.length > 0 && (
        <section className="mt-16">
          <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-wp-ocean" /><h2 className="font-display text-2xl">You may also love</h2></div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {related.map((p, i) => <ParcelCard key={p.id} parcel={p} index={i} />)}
          </div>
        </section>
      )}

      {/* sticky add to cart (mobile) */}
      <div className="fixed inset-x-0 bottom-[84px] z-30 px-3 md:hidden">
        <div className="glass-strong flex items-center justify-between gap-3 rounded-wp-lg border border-border p-3 shadow-wp-lift">
          <div>
            <p className="text-xs text-wp-ink-muted">{parcel.name}</p>
            <p className="price-num font-semibold text-wp-rose-strong">{formatINR(parcel.price)}</p>
          </div>
          <Button onClick={onAdd} data-testid="product-sticky-add-to-cart-button" className="rounded-xl bg-wp-rose-strong text-white">
            <ShoppingBag className="mr-1.5 h-4 w-4" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}

